/** @license DAFT Core v1.2.0
* Copyright 2020 Texas A&M Center for Applied Technology,
* Texas A&M Engineering Experiment Station, The Texas A&M
* University System.
*
* This source code/schema and any derivative work is Texas
* A&M Engineering Experiment Station intellectual property
* and protected by copyright law. Usage is restricted solely
* to the FARM Animal Care and Environmental Stewardship
* programs administered by the National Milk Producers
* Federation. Dissemination of this source code and/or
* derivative work creation is strictly prohibited without
* express written permission from the Texas A&M Engineering
* Experiment Station.
*/
import json_pointer from 'json-pointer';
import { Observable } from 'rxjs';
import { DFSExpression, DFSExpressionArray, Expression, FieldName, NIFormData } from './expressions';
import { ExpressionManager } from './ExpressionManager';
import { DFSValueType } from './form';
export type ProcessorFunction<T extends Expression<string>, R extends DFSValueType> = (exp: T, scope: string, expMgr: ExpressionManager) => Observable<R | null>;

const fieldNamePattern = /{(?:0|[1-9][0-9]*|\.\.)?(?:#|(?:\/(?:[^~\/\n{}\t]|~[01])*)*)}/i;

/**
 * Memoize a function. Keyed by stringified args. Not clearable or configurable, types won't work with generic functions.
 */
export function memoize<T extends any[], K>(fn: (...a: T) => K) {
  const cache: { [key: string]: K } = {};

  return function(...args: T): K {
    const key = JSON.stringify(args);

    if (cache[key]) {
        return cache[key];
    }

    const result = fn(...args);

    cache[key] = result;

    return result;
  };
}

/**
 * Field names are strings that match the format: '{/path/to/data}'.
 * @returns true if the target is a valid field name in a form, regardless of whether the path exists in the target form.
 * @example
 *```typescript
 * isFieldName('{/herd/profile/calves}') // true
 * ```
 */
export const isFieldName = function(param: any): param is FieldName {
  if (typeof param !== "string") {
    return false;
  }

  var r = param.match(fieldNamePattern);

  return r !== null && r[0] === param;
}

/**
 * Each expression in an array of expressions has to be track separately.
 */
export function isExpressionArray(param: DFSExpression): param is DFSExpressionArray {
  return Array.isArray(param);
}

/**
 * Removes the '{' and '}' from the target field name
 * @param rawField the wrapped pointer
 * @returns a JSON Pointer from a DFS field name
 * @example
 *```typescript
 * parseFieldIdentifier('{/herd/profile/calves}') // '/herd/profile/calves'
 * ```
 */
export function parseFieldIdentifier(rawField: string) {
  if (rawField.length === 0) {
    throw new Error("Found empty field identifier")
  }
  return rawField.slice(1, -1)
}

export const resolvePointer = function(path: string, scope: string) {
  const pathSlugs = path.split('/');
  const firstPathSlug = pathSlugs[0];

  // path can be relative, for example `{../../year}`
  if (scope && scope.length && firstPathSlug === '..') {
    const scopeSlugs = scope.slice(1).split('/');
    const relativePathSlugs = [];
    for(let i=0; i<pathSlugs.length; i++) {
      if (pathSlugs[i] == '..') {
        scopeSlugs.pop();
      } else {
        relativePathSlugs.push(pathSlugs[i]);
      }
    }
    scope = '/' + scopeSlugs.join('/');
    path = relativePathSlugs.join('/');
  }

  // For paths that are not relative to items within an array, exit early.
  if (isNaN(parseInt(firstPathSlug))) {
    if (scope && scope.length) {
      return scope + '/' + path;
    } else {
      return path;
    }
  }

  // Otherwise, deal with path like `{0/something}`  
  const tokens = json_pointer.parse(scope);
  const index = Number(firstPathSlug);

  for (let s = index; s > 0; s--) {

    if (tokens.length === 0) {
      throw new Error('Relative index out of bounds');
    }

    tokens.pop();
  }

  let a = tokens.join('/');
  const l = pathSlugs.slice(1).join('/');

  if (l.length > 0) {
    a = a.concat('/').concat(l);
  }

  if (a.length > 0 && !a.startsWith('/')) {
    a = '/'.concat(a);
  }

  return a;
}

export function dataPathsInExpression(expression: DFSExpression) {
  const r = JSON.stringify(expression);
  const t = r.match(new RegExp(fieldNamePattern, "gi"));

  return t === null ? [] : t
}
